import type { BrokerChangeSummary, Document } from '@/types/documents'
import { DocumentStatuses, DocumentType, inProgressBrokerChangesStatuses } from '@/types/documents'
import { isPendingEnvelope } from '@/utils/documents'
import type { RequestState } from '@/utils/restStore'
import { RequestStatus, handleRequest, initialRequestState } from '@/utils/restStore'
import { defineStore } from 'pinia'

import Api from '@/services/api'

interface State {
  documents: RequestState<Document[]>
}

const initialState = (): State => ({
  documents: initialRequestState()
})

const brokerChangeInProgress = (document: Document) =>
  document.type === DocumentType.Transfer &&
  !!document.additionalDetails?.contracts &&
  inProgressBrokerChangesStatuses.includes(document.status as DocumentStatuses)

const envelopeBrokerChanges = (document: Document): BrokerChangeSummary[] =>
  (document.additionalDetails?.documents ?? [])
    .filter((envelopeDocument) => envelopeDocument.type === DocumentType.BrokerChange)
    .map((envelopeDocument) => ({
      insurerName: envelopeDocument.details.insurerName ?? '',
      contracts: envelopeDocument.details.contracts ?? [],
      status: DocumentStatuses.WaitingForSignature
    }))

const brokerChangeSummaries = (document: Document): BrokerChangeSummary[] => {
  if (isPendingEnvelope(document)) return envelopeBrokerChanges(document)
  if (brokerChangeInProgress(document))
    return [
      {
        insurerName: document.additionalDetails?.insurerName ?? '',
        contracts: document.additionalDetails?.contracts ?? [],
        status: document.status as DocumentStatuses
      }
    ]
  return []
}

export const useDocumentsToSignStore = defineStore({
  id: 'documents-to-sign',
  state: initialState,
  actions: {
    async fetchDocuments() {
      await handleRequest(Api.getDocumentsToSign, (documents: RequestState<Document[]>) => {
        this.documents = documents
        if (this.documents.status === RequestStatus.RequestLoaded)
          this.documents.data = this.documents.data.filter(
            (document) => !(document.type === DocumentType.Termination && document.status === DocumentStatuses.Rejected)
          )
      })
    },
    async fetchCompanyDocuments(companyId: string) {
      await handleRequest(
        () => Api.getCompanyDocuments(companyId),
        (documents: RequestState<Document[]>) => {
          this.documents = documents
          if (this.documents.status === RequestStatus.RequestLoaded)
            this.documents.data = this.documents.data.filter(
              (document) =>
                !(document.type === DocumentType.Termination && document.status === DocumentStatuses.Rejected)
            )
        }
      )
    }
  },
  getters: {
    getAllDocuments: (state: State) => (): Document[] => {
      return state.documents.status === RequestStatus.RequestLoaded ? state.documents.data : []
    },
    getContractLifeProcedureDocuments:
      (state: State) =>
      (uuid: string): Document[] => {
        return state.documents.status === RequestStatus.RequestLoaded
          ? state.documents.data.filter(
              (document) =>
                document.contract && document.contract.uuid === uuid && document.type === DocumentType.LifeProcedure
            )
          : []
      },
    getBrokerChangesInProgress: (state: State) => (): BrokerChangeSummary[] => {
      return state.documents.status === RequestStatus.RequestLoaded
        ? state.documents.data.flatMap(brokerChangeSummaries)
        : []
    }
  }
})
